import { FC, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import classNames from 'classnames';

import BackToInquiriesButton from '@components/BackToInquiriesButton/BackToInquiriesButton';
import CardWithShadow from '@components/CardWithShadow/CardWithShadow';
import EmptyCard from '@components/EmptyCard/EmptyCard';
import HtmlContent from '@components/HtmlContent/HtmlContent';
import Loadable from '@components/Loadable/Loadable';
import SearchSelectBox from '@components/SearchSelectBox/SearchSelectBox';
import { IMedicationsItem } from '@components/SearchSelectBox/SearchSelectBox.interface';
import useToggle from '@hooks/useToggle';
import { IDietarySupplementTableData } from '@interfaces/supplements';
import urls from '@routes/urls';
import { dashboardOrgDataSelector } from '@store/features/dashboardOrgInfo/selectors';
import { handleCreateInquiryWithUserEnteredMedication } from '@store/features/inquiryDraft/createInquiry/actions';
import { createInquirySelector } from '@store/features/inquiryDraft/createInquiry/selectors';
import { removeCreateInquiry } from '@store/features/inquiryDraft/createInquiry/slice';
import { handleDeleteInquiryDraft } from '@store/features/inquiryDraft/deleteInquiryDraft/actions';
import { handleGetInquiryDetails } from '@store/features/inquiryDraft/inquiryDetails/actions';
import { removeInquiryDetails } from '@store/features/inquiryDraft/inquiryDetails/slice';
import {
  handleRemoveMedicationFromInquiryDraft,
} from '@store/features/inquiryDraft/removeMedicationFromInquiryDraft/actions';
import { removeDietarySupplementDetails } from '@store/features/inquiryDraft/supplementDetails/slice';
import { removeDietarySupplementSearch } from '@store/features/inquiryDraft/supplementSearch/slice';
import { handleGetMedicationDetails } from '@store/features/medications/medicationDetails/actions';
import {
  medicationDetailsActiveIdSelector,
  medicationDetailsErrorSelector,
  medicationDetailsIsLoadingSelector,
  medicationDetailsSelector,
} from '@store/features/medications/medicationDetails/selectors';
import { removeGetMedicationDetails } from '@store/features/medications/medicationDetails/slice';
import { handleGetMedicationSearch } from '@store/features/medications/medicationSearch/actions';
import {
  medicationSearchIsLoadingSelector,
  medicationSearchPaginationSelector,
  medicationSearchTableDataSelector,
} from '@store/features/medications/medicationSearch/selectors';
import { removeMedicationsSearch } from '@store/features/medications/medicationSearch/slice';
import { handleAddUserEnteredMedicationToInquiry } from '@store/features/medications/userEnteredMedication/actions';
import useDispatch from '@store/hooks/useDispatch';
import useDrugClasses from '@store/hooks/useDrugClasses';
import useMedicationSettings from '@store/hooks/useMedicationSettings';
import InquirySupplementsCard from '@views/InquiryDraftPage/InquirySupplementsCard/InquirySupplementsCard';
import NotificationModal from '@views/InquiryDraftPage/NotificationModal/NotificationModal';
import {
  INotificationModalData,
  TSupplementType,
} from '@views/InquiryDraftPage/NotificationModal/NotificationModal.interface';
import SubmitInquiryModal from '@views/InquiryDraftPage/SubmitInquiryModal/SubmitInquiryModal';
import SubmitInquirySuccessModal from '@views/InquiryDraftPage/SubmitInquirySuccessModal/SubmitInquirySuccessModal';
import UserEnteredMedicationModal from '@views/MedicationsPage/UserEnteredMedicationModal/UserEnteredMedicationModal';
import { EventType, useEventBus } from '@utils/eventBus';
import handleGetBannedMedications from '@utils/handleGetBannedMedications';

import AdditionalInformationCard from './AdditionalInformationCard/AdditionalInformationCard';
import InOutGrid from './InOutGrid/MedicationInOutGrid';
import MedicationSearchResultCard from './MedicationSearchResultCard/MedicationSearchResultCard';
import SubmitAnInquiryCTACard from './SubmitAnInquiryCTACard/SubmitAnInquiryCTACard';
import SupplementsNotificationsCard from './SupplementsNotificationsCard/SupplementsNotificationsCard';

import './style.scss';

const MedicationsPage: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { inquiryId: inquiryIdFromParams } = useParams();
  const inquiryId = Number(inquiryIdFromParams);
  const eventBus = useEventBus('medicationPage');
  const navigate = useNavigate();

  const drugClasses = useDrugClasses();
  const medicationSettings = useMedicationSettings();
  const medicationSearch = useSelector(medicationSearchTableDataSelector);
  const medicationSearchPagination = useSelector(medicationSearchPaginationSelector);
  const medicationLoading = useSelector(medicationSearchIsLoadingSelector);
  const medicationDetails = useSelector(medicationDetailsSelector);
  const medicationDetailsActiveId = useSelector(medicationDetailsActiveIdSelector);
  const medicationDetailsIsLoading = useSelector(medicationDetailsIsLoadingSelector);
  const medicationDetailsError = useSelector(medicationDetailsErrorSelector);
  const dashboardOrgInfo = useSelector(dashboardOrgDataSelector);
  const createdInquiry = useSelector(createInquirySelector);
  const canUseSupplementInquiry = dashboardOrgInfo?.canUseSupplementInquiry;
  const canUseMedicationInquiry = dashboardOrgInfo?.canUseDrugInquiry;
  const canUseDrugSearch = dashboardOrgInfo?.canUseDrugSearch;
  const medicationNotFoundText = dashboardOrgInfo?.medicationNotFoundText;
  const hasInOutCompBan = dashboardOrgInfo?.hasInOutCompBan;

  const [isAddMedicationModalOpen, onAddMedicationModalOpen, onAddMedicationModalClose] =
    useToggle();
  const [isSubmitInquiryModalOpen, onSubmitInquiryModalOpen, onSubmitInquiryModalClose] =
    useToggle();
  const [isSuccessModalOpen, onSuccessModalOpen, onSuccessModalClose] = useToggle();

  const [searchValue, setSearchValue] = useState<string>('');

  // This state keeps medicationId that supposed to be edited. It works only for draft inquires so far
  const [selectedUserEnteredMedicationId, setSelectedUserEnteredMedicationId] = useState<
    number | null
  >(null);

  // This state keeps medication in inquiry or inquiry itself that supposed to be removed
  const [itemToBeRemoved, setItemToBeRemoved] = useState<INotificationModalData | null>(null);

  // This state keeps medication that was selected by the search
  const [selectedMedication, setSelectedMedication] = useState<IMedicationsItem | null>(null);

  const administrations = useMemo(
    () =>
      medicationDetails?.rules
        ?.flatMap(rule =>
          rule.administrations.map(administration => ({
            ...administration,
            allowedIn: rule.allowedIn,
            allowedOutside: rule.allowedOutside,
            condition: rule.condition,
          })),
        )
        .sort((a, b) => (a.condition ? 1 : -1) - (b.condition ? 1 : -1))
        .filter(({ name }) => name !== 'NOTE'),
    [medicationDetails],
  );

  const rules = useMemo(() => medicationDetails?.rules, [medicationDetails]);

  const showBackButton = useMemo(() => {
    if (canUseSupplementInquiry) {
      return true;
    }
    return Boolean(canUseMedicationInquiry);
  }, [canUseSupplementInquiry, canUseMedicationInquiry]);

  const conditions = useMemo(() => {
    if (medicationDetails?.rules && medicationDetails?.rules?.length > 0) {
      return medicationDetails.rules.reduce((acc, rule) => {
        if (rule.condition && !acc.includes(rule.condition)) {
          acc.push(`${rule.condition}\n`);
        }
        return acc;
      }, [] as string[]);
    }
    return [];
  }, [medicationDetails]);

  const handleSelectMedication = (event: SyntheticEvent, value: IMedicationsItem | null): void => {
    setSelectedMedication(value);
    value ? dispatch(handleGetMedicationDetails(value.id)) : dispatch(removeMedicationsSearch());
  };

  const handleSearchMedication = (value: string): void => {
    dispatch(removeMedicationsSearch());
    setSearchValue(value.trim());
  };

  const searchMedication = useCallback(
    async (value: string): Promise<void> => {
      const page = medicationSearchPagination?.page || 1;
      const pageCount = medicationSearchPagination?.pageCount || 0;

      if ((page === pageCount && pageCount > 1) || value.length < 3) {
        return;
      }

      dispatch(
        handleGetMedicationSearch({
          search: value,
          page: page < pageCount ? page + 1 : page,
        }),
      );
    },
    [dispatch, medicationSearchPagination],
  );

  const clearWorkground = useCallback((): void => {
    dispatch(removeInquiryDetails());
    dispatch(removeDietarySupplementSearch());
    dispatch(removeDietarySupplementDetails());
    dispatch(removeCreateInquiry());
  }, [dispatch]);

  const handleAddToInquiry = useCallback(
    (userEnteredMedicationId: number | null = null): void => {
      if (!inquiryId) {
        dispatch(handleCreateInquiryWithUserEnteredMedication({ userEnteredMedicationId }));
      } else {
        dispatch(
          handleAddUserEnteredMedicationToInquiry({
            inquiryId,
            userEnteredMedicationId,
          }),
        );
      }
    },
    [dispatch, inquiryId],
  );

  const handleSelectMedicationToEdit = useCallback(
    (
      event: SyntheticEvent | null,
      value: IDietarySupplementTableData | null,
      userEnteredMedicationId: number | null = null,
    ): void => {
      if (userEnteredMedicationId) {
        setSelectedUserEnteredMedicationId(userEnteredMedicationId);
        onAddMedicationModalOpen();
      }
    },
    [onAddMedicationModalOpen],
  );

  const handleRemoveMedicationFromInquiry = useCallback((id: number) => {
    setItemToBeRemoved({
      itemId: id,
      type: TSupplementType.INQUIRY_ITEM,
    });
  }, []);

  const handleDeleteInquiry = useCallback((id: number) => {
    setItemToBeRemoved({
      itemId: id,
      type: TSupplementType.INQUIRY,
    });
  }, []);

  const onDeleteInquirySuccess = useCallback(() => {
    clearWorkground();
    navigate(urls.medications, {
      replace: false,
    });
  }, [clearWorkground, navigate]);

  const handleConfirmRemovalFromInquiry = useCallback(
    (data: INotificationModalData) => {
      if (!data) {
        return;
      }
      const id = Number(data.itemId);
      if (data.type === TSupplementType.INQUIRY) {
        dispatch(handleDeleteInquiryDraft({ id }));
      } else if (data.type === TSupplementType.INQUIRY_ITEM) {
        dispatch(handleRemoveMedicationFromInquiryDraft({ id }));
      }
    },
    [dispatch],
  );

  const handleCloseModalConfirmation = useCallback((): void => {
    setItemToBeRemoved(null);
  }, []);

  const handleRetryFetchSelectedMedicationDetails = useCallback(() => {
    medicationDetailsActiveId && dispatch(handleGetMedicationDetails(medicationDetailsActiveId));
  }, [dispatch, medicationDetailsActiveId]);

  const handleSubmitInquiryDone = useCallback((): void => {
    dispatch(removeInquiryDetails());
    navigate(urls.inquiries);
  }, [dispatch, navigate]);

  useEffect(() => {
    searchMedication(searchValue);
  }, [searchValue]);

  useEffect(() => {
    !medicationSearch.length && dispatch(removeGetMedicationDetails());
  }, [medicationSearch, dispatch]);

  useEffect(
    () => () => {
      dispatch(removeMedicationsSearch());
      dispatch(removeGetMedicationDetails());
      dispatch(removeCreateInquiry());
      dispatch(removeInquiryDetails());
      setSelectedMedication(null);
    },
    [dispatch],
  );

  useEffect(() => {
    if (!inquiryId) {
      clearWorkground();
    } else {
      dispatch(handleGetInquiryDetails({ id: inquiryId }));
    }
  }, [inquiryId, clearWorkground]);

  useEffect(() => {
    if (!createdInquiry) {
      return;
    }
    if (createdInquiry.supplementInquiriesDto?.length) {
      dispatch(removeCreateInquiry());
      return;
    }
    navigate(generatePath(urls.medicationsInquiryDetails, { inquiryId: `${createdInquiry.id}` }), {
      replace: false,
    });
  }, [navigate, createdInquiry]);

  useEffect(() => {
    eventBus.on(
      [
        EventType.INQUIRY_SUBMIT_SUCCESS,
        EventType.EDIT_NEW_MEDICATION_SUCCESS,
        EventType.ADD_NEW_MEDICATION_TO_INQUIRY_SUCCESS,
        EventType.REMOVE_MEDICATION_FROM_INQUIRY_DRAFT_SUCCESS,
      ],
      () => inquiryId && dispatch(handleGetInquiryDetails({ id: inquiryId })),
    );
    eventBus.on(EventType.INQUIRY_SUBMIT_SUCCESS, () => {
      onSubmitInquiryModalClose();
      onSuccessModalOpen();
    });
    eventBus.on(EventType.DELETE_INQUIRY_DRAFT_SUCCESS, onDeleteInquirySuccess);

    return () =>
      eventBus.off([
        EventType.INQUIRY_SUBMIT_SUCCESS,
        EventType.EDIT_NEW_MEDICATION_SUCCESS,
        EventType.ADD_NEW_MEDICATION_TO_INQUIRY_SUCCESS,
        EventType.REMOVE_MEDICATION_FROM_INQUIRY_DRAFT_SUCCESS,
        EventType.DELETE_INQUIRY_DRAFT_SUCCESS,
      ]);
  }, [eventBus, dispatch, inquiryId, onSubmitInquiryModalClose, onSuccessModalOpen]);

  return (
    <Container maxWidth="lg" className="page medications-page">
      {showBackButton && (
        <BackToInquiriesButton />
      )}

      <Typography variant="h1" className="medications-page__title">
        {t('medicationLookup.title')}
      </Typography>

      <Typography variant="body1" className="medications-page__description">
        {t('medicationLookup.description')}
      </Typography>

      <Typography variant="body1" className="medications-page__search-tip">
        {t('medicationLookup.searchTip')}
      </Typography>

      <Box className="medications-page__control-part">
        <Box className="medications-page__main">
          {Boolean(canUseDrugSearch) && (
            <CardWithShadow>
              <SearchSelectBox
                className="medications-page__select"
                title={t('medicationLookup.medicationName')}
                items={medicationSearch}
                selected={selectedMedication}
                onSelect={handleSelectMedication}
                onSearch={handleSearchMedication}
                onLoadMore={searchMedication}
                isLoading={medicationLoading}
                searchValue={searchValue}
                noOptionsText={
                  medicationNotFoundText && <HtmlContent>{medicationNotFoundText}</HtmlContent>
                }
                hasInOutCompBan={hasInOutCompBan}
              />
              {hasInOutCompBan && administrations && (
                <InOutGrid rules={rules} administrations={administrations} />
              )}
            </CardWithShadow>
          )}
          {Boolean(canUseMedicationInquiry) && (
            <div className="medications-page__inquiry-wrapper">
              <InquirySupplementsCard
                isSuplementary={false}
                onDeleteInquiry={handleDeleteInquiry}
                onSelectSupplement={handleSelectMedicationToEdit}
                onRemoveSupplementFromInquiry={handleRemoveMedicationFromInquiry}
                onSubmitInquiry={onSubmitInquiryModalOpen}
              />
            </div>
          )}
        </Box>

        <Box
          className={classNames('medications-page__right-aside', {
            'medications-page__right-aside_half-width': hasInOutCompBan,
          })}
        >
          <CardWithShadow className="medications-page__search-result">
            <Loadable
              isLoading={medicationDetailsIsLoading}
              error={medicationDetailsError && t('errors.medicationDetailsError')}
              onRetry={handleRetryFetchSelectedMedicationDetails}
            >
              {hasInOutCompBan ? (
                <AdditionalInformationCard
                  synonyms={medicationDetails?.synonyms}
                  drugClassIds={handleGetBannedMedications(
                    medicationDetails?.drugClassIds || [],
                    drugClasses,
                  )}
                  medicationDetails={medicationDetails}
                  conditions={conditions}
                  isBanned={!selectedMedication?.isAllowed}
                  isShowMedicationExceptionLink={!!medicationSettings?.showMedicationExceptionLink}
                  linkHref={medicationSettings?.medicationExceptionFileUrl}
                  linkTitle={medicationSettings?.medicationExceptionFileName}
                />
              ) : selectedMedication ? (
                <MedicationSearchResultCard
                  prescription={selectedMedication.name}
                  isBanned={!selectedMedication.isAllowed}
                  bannedText={medicationSettings?.bannedText || ''}
                  notBannedText={medicationSettings?.notBannedText || ''}
                  isShowMedicationExceptionLink={!!medicationSettings?.showMedicationExceptionLink}
                  bannedDrugClasses={handleGetBannedMedications(
                    selectedMedication.drugClassIds || [],
                    drugClasses,
                  )}
                  linkHref={medicationSettings?.medicationExceptionFileUrl}
                  linkTitle={medicationSettings?.medicationExceptionFileName}
                />
              ) : (
                <EmptyCard
                  icon="/images/img-medicationbottle.svg"
                  description={t('medicationLookup.emptyResultDescription')}
                  borderType="none"
                />
              )}
            </Loadable>
          </CardWithShadow>

          {Boolean(canUseMedicationInquiry) && (
            <>
              <Box className="medications-page__submit-an-inquiry-cta-card">
                <SubmitAnInquiryCTACard onClick={onAddMedicationModalOpen} />
              </Box>
              {Boolean(canUseSupplementInquiry) && (
                <Box className="medications-page__supplements-notifications-card">
                  <SupplementsNotificationsCard
                    icon="/images/img-dietarysupplement.svg"
                    link={{
                      href: urls.inquiryDraft,
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <UserEnteredMedicationModal
        medicationId={selectedUserEnteredMedicationId}
        onAddToInquiry={handleAddToInquiry}
        onClose={onAddMedicationModalClose}
        onExited={() => setSelectedUserEnteredMedicationId(null)}
        open={isAddMedicationModalOpen}
      />

      <NotificationModal
        dataInModal={itemToBeRemoved}
        onClose={handleCloseModalConfirmation}
        onExited={handleCloseModalConfirmation}
        onConfirm={handleConfirmRemovalFromInquiry}
        open={Boolean(itemToBeRemoved)}
        isMedicationMode
      />

      <SubmitInquiryModal
        open={isSubmitInquiryModalOpen}
        inquiryNumber={inquiryId}
        submitButtonTitle={t('submitInquiry.submitMedicationText')}
        onClose={onSubmitInquiryModalClose}
      />

      <SubmitInquirySuccessModal
        open={isSuccessModalOpen}
        onClose={onSuccessModalClose}
        title={t('submitInquiry.medicationConfirmationTitle')}
        onExited={handleSubmitInquiryDone}
      />
    </Container>
  );
};

export default MedicationsPage;
